import type { ScenarioVersionChildSolutionActionLiveChat } from "@dial-once/ich-seed";
import { parseUserAgent } from "../../../common/userAgent";
import {
  type ActionTypeAppOS,
  type DynamicUrls,
  type PushWrapperFn,
  ScenarioAnalyticsDocumentActionType,
  SolutionActionType,
} from "../../../types";
import {
  NodeType,
  OpenType,
  type ScenarioVersionChildSolutionActionApp,
  type ScenarioVersionChildSolutionActionContextualizedRedirection,
  type ScenarioVersionChildSolutionActionEmail,
  type ScenarioVersionChildSolutionActionInternalFaq,
  type ScenarioVersionChildSolutionActionPhoneNumber,
  type ScenarioVersionChildSolutionActionStore,
  type ScenarioVersionChildSolutionUrl,
} from "../../../types/api";
import {
  type ApiScenarioPageIntentTo,
  type ApiScenarioPageToPage,
  type ApiScenarioPageToSolution,
  ToPageType,
} from "../../../types/api/scenarioVersionChildren";
import { STORAGE_ANALYTIC_ID_KEY, STORAGE_SESSION_ID_KEY } from "../../constants";
import {
  checkIfUrlActionType,
  getInSessionStorage,
  getOsFromParsedUserAgent,
  handleContextualizedRedirection,
  handleEmailAction,
  handleIFrameAction,
  handleInternalFaqAction,
  handleIvrAction,
  handlePostalMailAction,
  handleSolutionAnalytic,
  handleStoreOrAppAction,
  handleUrlAction,
  postScenarioDetails,
} from "../../helpers";
import { context } from "../../stores";
import { runTransformationFunction } from "../context";
import { handleLiveChatAction } from "../router/handleLiveChatAction";

export type handleClickIntentArgs = {
  to: ApiScenarioPageIntentTo | null;
  intentId: string;
  shortCode: string | undefined;
  scenarioId: string | undefined;
  routerPush: PushWrapperFn;
  branch?: string;
  dynamicUrls: DynamicUrls;
};

const handleClickIntent = async ({
  to,
  shortCode,
  scenarioId,
  routerPush,
  intentId: fromIntentId,
  branch,
  dynamicUrls,
}: handleClickIntentArgs) => {
  let parsedUserAgent: ReturnType<typeof parseUserAgent> | undefined;
  try {
    parsedUserAgent = parseUserAgent(navigator.userAgent);
  } catch {
    parsedUserAgent = undefined;
  }
  const isNotAMobile = parsedUserAgent?.platform?.type !== 2;

  if (!scenarioId && !shortCode) return;
  if (to && to.type === ToPageType.solution && (to as ApiScenarioPageToSolution).solution) {
    const { solution, key: toPageKey, id, transformFct } = to as ApiScenarioPageToSolution;
    if (!solution) return;
    const { actionType } = solution;
    const isIvrSolutionAction =
      actionType === SolutionActionType.ivrCallHead ||
      actionType === SolutionActionType.ivrCallOriented;
    const isAppOrStoreSolutionAction =
      actionType === SolutionActionType.app || actionType === SolutionActionType.store;

    let solutionHasBeenExecuted = false;
    let updatedSolution = {};
    if (transformFct) {
      const result = runTransformationFunction({
        serializedFunction: transformFct,
        context: context.value,
        type: NodeType.solution,
        view: "",
        action: solution,
        analyticSessionId: getInSessionStorage(STORAGE_ANALYTIC_ID_KEY) || undefined,
        scenarioSessionId: getInSessionStorage(STORAGE_SESSION_ID_KEY) || undefined,
      });
      if (result && result.actionValues) {
        updatedSolution = result.actionValues;
      }
    }
    if (checkIfUrlActionType({ actionType })) {
      const { url } = solution as ScenarioVersionChildSolutionUrl;
      //TMP hotfix GMF bug
      void postScenarioDetails({
        type: ScenarioAnalyticsDocumentActionType.Push,
        shortCode,
        toPageKey,
        fromIntentId,
      });
      // TODO : Check if solution.id is base64 encoded or not
      solutionHasBeenExecuted = handleUrlAction({ url, ...updatedSolution });
    } else if (actionType === SolutionActionType.contextualizedRedirection && branch) {
      const { location, openType } =
        solution as ScenarioVersionChildSolutionActionContextualizedRedirection;
      await postScenarioDetails({
        type: ScenarioAnalyticsDocumentActionType.Push,
        shortCode,
        toPageKey,
        fromIntentId,
      });
      solutionHasBeenExecuted = handleContextualizedRedirection({
        branch,
        location,
        openType: openType || OpenType.NewWindow,
        dynamicUrls,
        ...updatedSolution,
      });
    } else if (actionType === SolutionActionType.email) {
      const {
        to: toEmail,
        subject,
        body,
        cc,
        bcc,
      } = solution as ScenarioVersionChildSolutionActionEmail;
      await postScenarioDetails({
        type: ScenarioAnalyticsDocumentActionType.Push,
        shortCode,
        toPageKey,
        fromIntentId,
      });
      solutionHasBeenExecuted = handleEmailAction({
        to: toEmail,
        subject,
        body,
        cc,
        bcc,
        ...updatedSolution,
      });
    } else if (
      actionType === SolutionActionType.internalFAQ &&
      (solution as ScenarioVersionChildSolutionActionInternalFaq).pageKey
    ) {
      const { pageKey } = solution as ScenarioVersionChildSolutionActionInternalFaq;
      solutionHasBeenExecuted = await handleInternalFaqAction({
        pageKey,
        shortCode,
        scenarioId,
        routerPush,
        fromIntentId,
        ...updatedSolution,
      });
    } else if (isIvrSolutionAction) {
      const { phoneNumber } = solution as ScenarioVersionChildSolutionActionPhoneNumber;
      const toPageKey = `solution-${id}`;
      solutionHasBeenExecuted = await handleIvrAction({
        id,
        shortCode,
        scenarioId,
        routerPush,
        phoneNumber,
        toPageKey,
        fromIntentId,
        ...updatedSolution,
      });
    } else if (actionType === SolutionActionType.postalMail) {
      const toPageKey = `solution-${id}`;
      solutionHasBeenExecuted = await handlePostalMailAction({
        id,
        shortCode,
        scenarioId,
        routerPush,
        toPageKey,
        fromIntentId,
        ...updatedSolution,
      });
    } else if (isAppOrStoreSolutionAction) {
      const { actionType } = solution;
      const { iosLink, androidLink } = solution as ScenarioVersionChildSolutionActionApp;
      const { appStoreLink, playStoreLink } = solution as ScenarioVersionChildSolutionActionStore;
      const toPageKey = `solution-${id}`;
      solutionHasBeenExecuted = await handleStoreOrAppAction({
        isNotAMobile,
        id,
        shortCode,
        scenarioId,
        actionType: actionType as SolutionActionType.app | SolutionActionType.store,
        routerPush,
        appStoreLink,
        playStoreLink,
        os: (parsedUserAgent
          ? getOsFromParsedUserAgent(parsedUserAgent)
          : "other") as ActionTypeAppOS,
        androidLink,
        iosLink,
        toPageKey,
        fromIntentId,
        ...updatedSolution,
      });
    } else if (actionType === SolutionActionType.liveChat) {
      const { url } = solution as ScenarioVersionChildSolutionActionLiveChat;
      handleLiveChatAction({ url, ...updatedSolution });
      /* Two cases are possible, if it is an Iadvize solution, the helper above won't do anything,
      but we still have to send the analytics, so solutionHasBeenExecuted must be set to true.  */
      solutionHasBeenExecuted = true;
    } else if (actionType === SolutionActionType.iFrame) {
      solutionHasBeenExecuted = await handleIFrameAction({
        id,
        shortCode,
        scenarioId,
        routerPush,
        toPageKey,
        fromIntentId,
        ...updatedSolution,
      });
    }
    if (solutionHasBeenExecuted) {
      await handleSolutionAnalytic({ type: actionType, solutionId: id, shortCode });
    }
  } else if (to && to.type === ToPageType.page) {
    const url = shortCode
      ? `/c/${shortCode}/${(to as ApiScenarioPageToPage).key}`
      : `/p/${scenarioId}/${(to as ApiScenarioPageToPage).key}`;
    await routerPush(url, { toPageKey: (to as ApiScenarioPageToPage).key, fromIntentId });
  } else {
    // eslint-disable-next-line no-console
    console.error("Invalid To provided");
  }
};

export default handleClickIntent;
